/**
 * Stonelux Sample Manager
 * Handles local storage, modal rendering, and cart submission for sample packs.
 */
class SampleManager {
  constructor() {
    this.storageKey = 'stonelux_samples';
    this.maxSamples = 6;
    
    // DOM Elements
    this.addButtons = document.querySelectorAll('[data-add-sample]');
    this.gridContainer = document.querySelector('[data-sample-grid]');
    this.checkoutBtn = document.querySelector('[data-sample-checkout]');
    this.headerWrappers = document.querySelectorAll('[data-header-sample-wrapper]');
    this.headerCounters = document.querySelectorAll('[data-header-sample-count]');
    this.warningText = document.querySelector('[data-sample-warning]');
    this.messageText = document.querySelector('[data-sample-message]');

    // Initialize unconditionally so the header counts update on every page
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateUI();
  }

  getSamples() {
    const samples = localStorage.getItem(this.storageKey);
    return samples ? JSON.parse(samples) : [];
  }

  saveSamples(samples) {
    localStorage.setItem(this.storageKey, JSON.stringify(samples));
    this.updateUI();
  }

  bindEvents() {
    document.body.addEventListener('click', (e) => {
      
      // 1. "Order Sample" Button Click
      const addBtn = e.target.closest('[data-add-sample]');
      if (addBtn) {
        e.preventDefault();
        this.handleAddClick(addBtn);
      }

      // 2. "Remove" icon in the modal grid
      const removeBtn = e.target.closest('[data-remove-sample]');
      if (removeBtn) {
        e.preventDefault();
        const index = removeBtn.dataset.removeSample;
        this.removeSample(parseInt(index));
      }

      // 3. "Your Samples" Header Button Click
      const headerBtn = e.target.closest('[data-header-sample-wrapper] button');
      if (headerBtn) {
        e.preventDefault();
        MicroModal.show('sample-modal');
      }

      // 4. "Info" Icon Click
      const infoBtn = e.target.closest('[data-micromodal-trigger="sample-info-modal"]');
      if (infoBtn) {
        e.preventDefault();
        MicroModal.show('sample-info-modal');
      }

      // 5. Final Add to Cart logic
      const checkoutBtnClick = e.target.closest('[data-sample-checkout]');
      if (checkoutBtnClick) {
        e.preventDefault();
        // Prevent clicking if it's disabled
        if (!checkoutBtnClick.disabled) {
          this.submitToCart();
        }
      }

    });
  }

  handleAddClick(btn) {
    const samples = this.getSamples();
    
    // If full, just open the modal to show the warning
    if (samples.length >= this.maxSamples) {
      this.warningText.classList.remove('is-hidden');
      this.messageText.classList.add('is-hidden');
      MicroModal.show('sample-modal');
      return;
    }

    // Add new sample
    const newSample = {
      title: btn.dataset.sampleTitle,
      variant: btn.dataset.sampleVariant,
      image: btn.dataset.sampleImage
    };

    samples.push(newSample);
    this.saveSamples(samples);
    
    // Reset warnings and open modal
    this.warningText.classList.add('is-hidden');
    this.messageText.classList.remove('is-hidden');
    MicroModal.show('sample-modal');
  }

  removeSample(index) {
    const samples = this.getSamples();
    samples.splice(index, 1);
    this.saveSamples(samples);
    
    // Clear warning if we dropped below max
    if (samples.length < this.maxSamples) {
      this.warningText.classList.add('is-hidden');
      this.messageText.classList.remove('is-hidden');
    }
  }

  updateUI() {
    const samples = this.getSamples();
    
    // 1. Update Header Button(s)
    const headerWrappers = document.querySelectorAll('[data-header-sample-wrapper]');
    const headerCounters = document.querySelectorAll('[data-header-sample-count]');
    
    if (samples.length > 0) {
      headerWrappers.forEach(wrap => wrap.classList.remove('is-hidden'));
      headerCounters.forEach(counter => counter.textContent = samples.length);
    } else {
      headerWrappers.forEach(wrap => wrap.classList.add('is-hidden'));
    }

    // 2. Update Checkout & Keep Browsing Button States
    const checkoutBtns = document.querySelectorAll('[data-sample-checkout]');
    const keepBrowsingBtns = document.querySelectorAll('[data-sample-keep-browsing]');
    
    // Disable the Add to Cart button unless exactly 6 swatches are chosen
    checkoutBtns.forEach(btn => {
      btn.disabled = samples.length !== this.maxSamples;
    });

    if (keepBrowsingBtns.length && checkoutBtns.length) {
      if (samples.length < this.maxSamples) {
        keepBrowsingBtns.forEach(btn => {
          btn.classList.add('btn--primary', 'width-66');
          btn.classList.remove('btn--outline', 'width-33');
        });
        checkoutBtns.forEach(btn => {
          btn.classList.add('btn--outline', 'width-33');
          btn.classList.remove('btn--primary', 'width-66');
        });
      } else {
        keepBrowsingBtns.forEach(btn => {
          btn.classList.add('btn--outline', 'width-33');
          btn.classList.remove('btn--primary', 'width-66');
        });
        checkoutBtns.forEach(btn => {
          btn.classList.add('btn--primary', 'width-66');
          btn.classList.remove('btn--outline', 'width-33');
        });
      }
    }

    // 3. Render Modal Grid
    if (this.gridContainer) {
      this.gridContainer.innerHTML = '';
      
      for (let i = 0; i < this.maxSamples; i++) {
        const slot = document.createElement('div');
        slot.className = 'sample-slot-container flex flex-col';
        
        if (samples[i]) {
          slot.innerHTML = `
            <div class="sample-slot relative bg-gray-100 mb-2 aspect-square">
              <img src="${samples[i].image}" alt="${samples[i].title}" class="w-full h-full object-cover" />
              <button type="button" data-remove-sample="${i}" class="sample-remove-btn" aria-label="Remove swatch">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <span class="sample-title-text font-bold text-center leading-tight uppercase" style="font-size: 11px;">${samples[i].title}</span>
          `;
        } else {
          slot.innerHTML = `<div class="sample-slot bg-gray-100 aspect-square"></div>`;
        }
        this.gridContainer.appendChild(slot);
      }
    }
  }

  submitToCart() {
    const samples = this.getSamples();
    
    // Stop the submission if they haven't picked exactly 6
    if (samples.length !== this.maxSamples) return;

    // Dynamically grab the specific checkout button to avoid cloned DOM issues
    const activeCheckoutBtn = document.querySelector('[data-sample-checkout]');
    if (!activeCheckoutBtn) return;

    // Ensure the ID is parsed as an integer for Shopify's API
    const baseVariantId = parseInt(activeCheckoutBtn.dataset.baseVariant, 10);

    // SAFETY CHECK: If the ID is missing, alert you so you don't have to guess
    if (!baseVariantId || isNaN(baseVariantId)) {
      console.error('Swatch Pack Error: Missing Variant ID. Please check Theme Settings.');
      alert('Missing Swatch Product Configuration. Please select a Base Sample Product in your Theme Settings.');
      return;
    }

    // Set button to loading state
    activeCheckoutBtn.classList.add('loading');
    activeCheckoutBtn.disabled = true;

    // Build Line Item Properties
    let properties = {};
    samples.forEach((sample, index) => {
      properties[`Swatch ${index + 1}`] = `${sample.title}`;
    });

    // Generate a timestamp identical to the app's output
    const timestamp = new Date().toUTCString();
    properties['Terms & Conditions accepted'] = timestamp;

    // Shopify AJAX Add API
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: baseVariantId,
          quantity: 1,
          properties: properties
        }]
      })
    })
    .then(response => {
      if (!response.ok) {
        // If Shopify throws a 422, grab the specific error message
        return response.json().then(err => { throw err; });
      }
      return response.json();
    })
    .then(() => {
      // Clear samples from storage
      localStorage.removeItem(this.storageKey);
      this.updateUI();
      
      // Close Modal and trigger Theme's native cart drawer
      MicroModal.close('sample-modal');
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
      document.querySelector('.cart__drawer').dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
    })
    .catch((error) => {
      console.error('Error adding swatches to cart:', error);
      activeCheckoutBtn.classList.remove('loading');
      activeCheckoutBtn.disabled = false;
      alert(`Could not add to cart: ${error.description || error.message || 'Unknown error'}`);
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new SampleManager();
});